import type { PresentableTaskResource, ToolConfig } from '@timelyapp/integration-cloud-api-spec';
import { deriveAiStatus } from '@timelyapp/integration-cloud-sdk/utils/ai_predictions/derive-ai-status';
import { uniq } from 'lodash-es';
import { showToast } from 'redux/modules/toasts.actions';
import { getHourDraftDay } from 'redux/selectors/hours.draft.base';
import type { IState } from 'redux/state';
import timelyAnalytics from 'tracking/timely-analytics';
import debug, { LogLevel } from 'utils/debug';
import { fromISO } from 'utils/luxon-helpers';
import { keyBy } from 'utils/typesafe';
import { updatePredictionsMeta } from '~domains/ai/predictions/redux/predictions.slice';

// Lazy imports to break circular dependencies
const getHoursActions = () => import('redux/modules/hours.actions');
const getPredictionsActions = () => import('~domains/ai/predictions/redux/predictions.actions');
const getPredictionsSelectors = () => import('~domains/ai/predictions/redux/predictions.selectors');

// Lazy imports to break circular dependencies (these modules import ticApi back)
const getAiPreferencesActions = () =>
  import('~domains/ai/preferences/redux/ai.preferences.actions');
const getIntegrationsSlice = () => import('~domains/integrations/redux/tic.integrations.slice');
const getMemoriesSelectors = () => import('../tic.memories.selectors');

import { setRequestInProgress, setUserPrompt } from '../agent/tic.ai.agent.slice';
import { appendTasks } from '../tasks/tic.tasks.slice';
import { ticApi as rawTicApi } from './GENERATED_ARTIFACT';

// Re-export types
export type {
  GetAiPreferencesApiResponse,
  SaveAiPreferencesApiArg,
  SaveAiPreferencesApiResponse,
} from './GENERATED_ARTIFACT';

/**
 * Enhancements to the generated API slice can be done here.
 * @link https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting#enhanceendpoints
 */
export const ticApi = rawTicApi.enhanceEndpoints({
  addTagTypes: [
    'Memory',
    'PredictedIntervals',
    'AiStatus',
    'AiPreferences',
    'AiWorkspacePreferences',
    'AiWorkspaceDefaults',
    'AiAgent',
    'Tools',
    'Notifications',
    'GeneratedNote',
    'DefaultToolConfigs',
    'DiffsList',
    'DiffDetails',
    'ConnectionJobs',
    'IntegrationDetails',
    'Integrations',
    'SubscribableResources',
    'ConnectionCustomSettingsFields',
    'TimesheetProposal',
    'TimesheetProposals',
    'ConnectionWebhooks',
    'ProjectSuggestions',
  ],

  endpoints: {
    updateConnectionId: {
      invalidatesTags: ['IntegrationDetails', 'Integrations', 'ConnectionCustomSettingsFields'],
    },
    getConnectionProviderCustomSettingsFields: {
      providesTags: ['ConnectionCustomSettingsFields'],
    },
    getIntegrationDetails: {
      providesTags: ['IntegrationDetails'],
    },
    getIntegrations: {
      providesTags: ['Integrations'],
    },
    getSubscribableResources: {
      providesTags: ['SubscribableResources'],
      onQueryStarted: async (args, { dispatch }) => {
        const { setSubscriptionQueryArgs } = await getIntegrationsSlice();
        dispatch(setSubscriptionQueryArgs(args));
      },
    },
    updateBatchOfSubscriptions: {
      invalidatesTags: ['SubscribableResources', 'ConnectionCustomSettingsFields'],
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        // Follow batchSetNotificationsState pattern for optimistic updates
        const queries = ticApi.util.selectCachedArgsForQuery(
          getState(),
          'getSubscribableResources'
        );
        const usableQueryArgs = queries?.length === 1 ? queries[0] : null;

        // If we have !== 1 cached query from `getSubscribableResources`, invalidate the cache
        if (!usableQueryArgs) {
          dispatch(ticApi.util.invalidateTags(['SubscribableResources']));
        } else {
          // Optimistic update: apply changes to cached data
          const changesMap = keyBy(args.body.subscriptions, 'subscription_id');
          const patchResult = dispatch(
            ticApi.util.updateQueryData('getSubscribableResources', usableQueryArgs, draft => {
              draft.subscriptions = draft.subscriptions.map(subscription => {
                const change = changesMap[subscription.subscription_id];
                if (!change) return subscription;

                // Apply change to main subscription
                const updatedSub = { ...subscription, ...change };

                // Apply changes to sub_subscriptions if they exist
                if (updatedSub.sub_subscriptions) {
                  updatedSub.sub_subscriptions = updatedSub.sub_subscriptions.map(sub => {
                    const subChange = changesMap[sub.subscription_id];
                    return subChange ? { ...sub, ...subChange } : sub;
                  });
                }

                return updatedSub;
              });
            })
          );

          // Clear pending changes optimistically
          const { clearPendingChanges, restorePendingChanges } = await getIntegrationsSlice();
          dispatch(clearPendingChanges());

          // Rollback on failure
          queryFulfilled.catch(() => {
            patchResult.undo();
            // Restore pending changes on failure
            dispatch(restorePendingChanges(args.body.subscriptions));
            dispatch(showToast({ message: 'Failed to save subscription changes', type: 'danger' }));
          });
        }

        // Handle success
        queryFulfilled.then(() => {
          dispatch(
            showToast({ message: 'Subscription changes saved successfully', type: 'success' })
          );
        });
      },
    },
    providerOperationId: {
      invalidatesTags: [
        'IntegrationDetails',
        'Integrations',
        'ConnectionWebhooks',
        'ConnectionJobs',
      ],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          // Track operation in Redux store
          if (data?.job_id) {
            const { setIntegrationJobStatus } = await getIntegrationsSlice();
            dispatch(
              setIntegrationJobStatus({
                tool_id: arg.provider,
                connection_id: arg.connectionId,
                job_id: data.job_id,
                requested_operation_id: arg.operationId,
                status: 'started',
                message: data.message || `Starting ${arg.provider}/${arg.operationId}...`,
              })
            );
          }

          // Handle synchronous operations (legacy behavior)
          const beforeTitle = `Initiating ${arg.operationId}`;
          const beforeMsg = data?.message || `Import in progress`;
          dispatch(showToast({ title: beforeTitle, message: beforeMsg }));

          debug.template.integration(` ${beforeTitle}`, data);

          // Finalization is handled after sync completes, not immediately: it must run
          // after async job completion to avoid race conditions. Users can "Resume Later"
          // and complete setup once sync is done. (TT-3896)
        } catch (error) {
          const errorMsg = error?.message || 'Integration job failed';
          dispatch(showToast({ message: errorMsg, type: 'danger' }));
        }
      },
    },
    finalizeIntegrationSetup: {
      invalidatesTags: ['IntegrationDetails', 'Integrations', 'ConnectionWebhooks'],
      async onQueryStarted(_args, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          debug.template.integration(`Finalized integration setup (with side effects)`, data);
        } catch (error) {
          const message = 'Failed to finalize integration setup';
          dispatch(showToast({ message, type: 'danger' }));
        }
      },
    },
    regenerateFullDiff: {
      async onQueryStarted(_args, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          debug.template.integration(`Regenerated full diff`, data);
        } catch (error) {
          const message = 'Failed to regenerate full diff';
          dispatch(showToast({ message, type: 'danger' }));
        }
      },
    },
    getDiffsList: {
      providesTags: (result, error, arg) => [
        { type: 'DiffsList' as const, id: arg.connectionId },
        'DiffsList',
      ],
    },
    getDiffDetails: {
      providesTags: (result, error, arg) => [
        { type: 'DiffDetails' as const, id: arg.diffId },
        'DiffDetails',
      ],
    },
    getConnectionJobs: {
      providesTags: (result, error, arg) => [
        { type: 'ConnectionJobs' as const, id: arg.connectionId },
        'ConnectionJobs',
      ],
    },
    getConnectionWebhooks: {
      providesTags: (result, error, arg) => [
        { type: 'ConnectionWebhooks', id: arg.connectionId },
        'ConnectionWebhooks',
      ],
    },
    takeoverWebhooks: {
      invalidatesTags: (result, error, arg) => [
        { type: 'ConnectionWebhooks', id: arg.connectionId },
        'ConnectionWebhooks',
      ],
    },
    reinstallConnectionWebhooks: {
      invalidatesTags: (result, error, arg) => [
        { type: 'ConnectionWebhooks', id: arg.connectionId },
        'ConnectionWebhooks',
      ],
    },
    disconnectConnectionId: {
      invalidatesTags: (result, error, arg) => [
        { type: 'ConnectionWebhooks', id: arg.connectionId },
        'IntegrationDetails',
        'Integrations',
      ],
    },
    resetConnectionId: {
      invalidatesTags: (result, error, arg) => [
        { type: 'ConnectionWebhooks', id: arg.connectionId },
        'IntegrationDetails',
        'Integrations',
      ],
    },
    /**
     * Notifications
     */
    getNotifications: {
      providesTags: ['Notifications'],
    },
    /**
     * Changes will be optimistic updates if we have a single cached query for `getNotifications`,
     * else, we'll invalidate the cache for re-fetch.
     */
    batchSetNotificationsState: {
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        const queries = ticApi.util.selectCachedArgsForQuery(getState(), 'getNotifications');
        const usableQueryArgs = queries?.length === 1 ? queries[0] : null;

        // If we have !== 1 cached query from `getNotifications`, invalidate the cache
        // since we don't know exactly which cache to update (but feel free to add the complexity of needed)
        if (!usableQueryArgs) {
          dispatch(ticApi.util.invalidateTags(['Notifications']));
        } else {
          const notificationStateMap = keyBy(args.body.notification_states, 'notification_id');
          const patchResult = dispatch(
            ticApi.util.updateQueryData('getNotifications', usableQueryArgs, draft => {
              const notifications = draft.notifications.map(notification => {
                const newState = notificationStateMap[notification.id] || null;
                if (!newState) return notification;
                return { ...notification, state: { ...notification.state, ...newState } };
              });
              Object.assign(draft, { notifications });
            })
          );
          queryFulfilled.catch(patchResult.undo); // Rollback on fail
        }
      },
    },
    /**
     * Predictions
     */
    getAiPredictionsInDateRange: {
      providesTags: ['PredictedIntervals'],
      /** Collect latest "predicted for date" meta included in this payload */
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(updatePredictionsMeta(data.meta)); // TODO: Remove this stuff, right?
        } catch (error) {}
      },
    },
    rejectAiPrediction: {
      // TODO: Client-side updates instead.
      invalidatesTags: ['PredictedIntervals'],
      async onQueryStarted(arg) {
        timelyAnalytics.trackTic('TIC+AI Reject Prediction', arg.body);
      },
    },
    /**
     * @important After loading AI status, internal triggers will determine how to automate the next AI step or action.
     * ONLY WORKS in single-day context (but can be adjusted to work with multiple days).
     *
     * PS: API doesn't return "irrelevant" states, only the current event states.
     *    e.g, never `training_uninitialized` AND `training_requested` at the same time
     */
    getAiPredictionStatusForDateRange: {
      keepUnusedDataFor: 1 * 60, // clear AI data "not in view" after 1min
      providesTags: ['AiStatus'],
      async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
        const state = getState() as IState; // Type error, but we know it's IState

        // ❌ Opt out early if we don't have ONE date to work with
        // ⚠️ Week/Month View currently only supported when HourEditor mode is open.
        const date = arg.fromDate === arg.toDate ? arg.fromDate : getHourDraftDay(state);

        if (!date) {
          debug.template.ai('Skipping, only intended for single day mode', arg);
          return;
        }

        const { data } = await queryFulfilled;

        // Le sigh, the camelCase vs snake_case via oAPI->RTKQ are annoying AF.
        const args_cased = {
          timely_account_id: arg.timelyAccountId,
          timely_user_id: arg.timelyUserId,
          from_date: date, // arg.fromDate // <-- when >1d gets supported
          to_date: date, // arg.toDate // <-- when >1d gets supported
        };

        // New AI status logic
        const { getTicAiPreferences } = await getPredictionsSelectors();
        const { getLastRelevantMemoryEndingForAiStatus } = await getMemoriesSelectors();
        const { nextAction } = deriveAiStatus(args_cased, data, {
          ai_enabled: !!getTicAiPreferences(state)?.enabled,
          last_memory_updated_at: getLastRelevantMemoryEndingForAiStatus(state),
          debugCallback: debug.template.ai, // To avoid console.log spamming end-users
        });

        // Execute nextAction, if any..
        const body = args_cased;
        const { safelyDelayRefreshAiPredictionsInDateRange, safelyDelayRetrainAiModel } =
          await getPredictionsActions();
        switch (nextAction) {
          case 'RUN_FIRST_INFERENCE_ON_DATE':
            dispatch(
              safelyDelayRefreshAiPredictionsInDateRange({ body, delayStrategy: 'instant' })
            );
            break;
          case 'RE_RUN_INFERENCE_ON_DATE':
            dispatch(
              safelyDelayRefreshAiPredictionsInDateRange({ body, delayStrategy: 'throttle' })
            );
            break;
          case 'RE_RUN_MODEL_TRAINING':
            dispatch(safelyDelayRetrainAiModel({ body, delayStrategy: 'debounce' }));
            break;
          default:
            break;
        }
      },
    },
    getAiPreferences: {
      providesTags: ['AiPreferences'],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          // If no AI preferences found, we'll initialize AI with defaults.
          // ref https://linear.app/timelyapp/issue/TT-2522/automatically-enable-ticai-for-users-by-default
          // PS: Per `useTicAiDataSubscriber`, we'll only be here if AI is allowed in the first place.
          if (!data.id) {
            debug.template.ai('No AI preferences found, triggering initialization...');
            const { initializeAiPreferences } = await getAiPreferencesActions();
            dispatch(initializeAiPreferences());
          }
        } catch (error) {
          debug.template.ai('Failed to load AI preferences:', error);
        }
      },
    },
    resetAiModel: {
      invalidatesTags: ['AiStatus'],
    },
    refreshAiPredictionsInDateRange: {
      onQueryStarted: async args => {
        // @ts-expect-error - args doesn't have the controls typed:
        const delayStrategy = args.delayStrategy || '??';
        debug.template.ai(`✅ Executing ${delayStrategy} inference..`, args);
      },
      onCacheEntryAdded: async (arg, { cacheDataLoaded, dispatch }) => {
        try {
          const { data } = await cacheDataLoaded;
          if (data.log) {
            const { injectAiLogToStatusForDateRange } = await getPredictionsActions();
            dispatch(injectAiLogToStatusForDateRange(data.log));
          }
        } catch (error) {}
      },
    },
    retrainAiModel: {
      onQueryStarted: async args => {
        debug.template.ai('✅ Executing re-training ..', args);
      },
      onCacheEntryAdded: async (arg, { cacheDataLoaded, dispatch }) => {
        try {
          const { data } = await cacheDataLoaded;
          if (data.log) {
            const { injectAiLogToStatusForDateRange } = await getPredictionsActions();
            dispatch(injectAiLogToStatusForDateRange(data.log));
          }
        } catch (error) {}
      },
    },
    /**
     * Calling this endpoint will always reload AI status from server, triggering the AI trigger system
     * @see ticApi.endpoints.getAiPredictionStatusForDateRange.onQueryStarted (above, in this file)
     */
    saveAiPreferences: {
      invalidatesTags: ['AiStatus'], // Server-side may decide on side-effects, so to be safe..
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          // Only update database-level properties if we don't have them locally
          // We can't optimistically update everything; may be debouncing another change already updated.
          if (!args.body?.id) {
            const { updateAiPreferences } = await getAiPreferencesActions();
            dispatch(updateAiPreferences({ id: data.id, updated_at: data.updated_at }));
          }
        } catch (error) {
          dispatch(showToast({ message: 'Failed to save AI preferences', type: 'danger' }));
          debug.template.ai('Failed to save AI preferences:', error);
        }
      },
    },
    // Workspace-level preferences
    getAiWorkspacePreferences: {
      providesTags: ['AiWorkspacePreferences'],
    },
    saveAiWorkspacePreferences: {
      invalidatesTags: ['AiWorkspacePreferences', 'AiStatus'],
    },
    getAiWorkspaceDefaults: {
      providesTags: ['AiWorkspaceDefaults'],
    },
    saveAiWorkspaceDefaults: {
      invalidatesTags: ['AiWorkspaceDefaults', 'AiWorkspacePreferences'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistic update: immediately reflect the new workspace defaults
        const patchDefaults = dispatch(
          ticApi.util.updateQueryData(
            'getAiWorkspaceDefaults',
            { timelyAccountId: arg.timelyAccountId },
            draft => {
              Object.assign(draft, arg.body);
            }
          )
        );
        // Also optimistically update workspace preferences locked_fields
        const patchPrefs = dispatch(
          ticApi.util.updateQueryData(
            'getAiWorkspacePreferences',
            { timelyAccountId: arg.timelyAccountId },
            draft => {
              const lockedFields = arg.body.locked_fields ?? [];
              draft.locked_fields = lockedFields;
              if (lockedFields.length > 0 && arg.body.timesheet_assistant) {
                for (const field of lockedFields) {
                  if (field in arg.body.timesheet_assistant) {
                    (draft.timesheet_assistant as Record<string, unknown>)[field] = (
                      arg.body.timesheet_assistant as Record<string, unknown>
                    )[field];
                  }
                }
              }
            }
          )
        );
        queryFulfilled.catch(() => {
          patchDefaults.undo();
          patchPrefs.undo();
        });
      },
    },
    resetEverythingAi: {
      invalidatesTags: ['AiPreferences', 'AiStatus', 'PredictedIntervals'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        timelyAnalytics.trackTic('TIC+AI Reset Everything', {});
        debug.template.ai('🧼 Clean wipe, resetting to initial settings. AI disabled');

        // Ensure AI is immediately disabled in local state.
        const patchResult = dispatch(
          ticApi.util.updateQueryData('getAiPreferences', undefined, draft => {
            return { ...draft, enabled: false, id: null, preset: null };
          })
        );

        // Rollback on failure
        queryFulfilled.catch(patchResult.undo);
      },
      /** We toast to the clearing of AI data */
      onCacheEntryAdded: async (arg, { cacheDataLoaded, dispatch }) => {
        try {
          const { data } = await cacheDataLoaded;
          dispatch(showToast({ message: data.message || 'AI data cleared', type: 'info' }));
        } catch (error) {
          dispatch(
            showToast({ message: error?.message || 'Failed to clear AI data', type: 'danger' })
          );
        }
      },
    },
    /**
     * AI Agent
     */
    getAssistantResponse: {
      providesTags: ['AiAgent'],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        dispatch(setUserPrompt(arg.message));
        dispatch(setRequestInProgress(true));
        try {
          const { data } = await queryFulfilled;
          dispatch(
            showToast({
              message: data.message || 'AI agent request completed',
              type: 'success',
            })
          );
        } catch (error) {
          dispatch(setRequestInProgress(false));
          dispatch(
            showToast({
              message: 'There was an error processing the request to the AI agent',
              type: 'danger',
            })
          );
        }
      },
    },
    /**
     * Tools
     */
    getTools: {
      providesTags: ['Tools'],
    },
    getToolId: {
      providesTags: result => (result?.tool ? [{ type: 'Tools', id: result.tool.id }] : ['Tools']),
    },
    saveToolConfig: {
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        const { data } = await queryFulfilled;
        const toolConfig = typeof data === 'object' ? data : undefined;

        // Update existing tool config in the store (or add, if we haven't already optimistically updated)
        if (toolConfig) {
          const patchResult = dispatch(
            ticApi.util.updateQueryData('getToolConfigs', undefined, draftConfigs => {
              let upserted = false;
              draftConfigs.configs = draftConfigs.configs.map(config => {
                const existingIdentifier = config.tool_id || config.tool_raw;
                const incomingIdentifier = toolConfig.tool_id || toolConfig.tool_raw;

                if (existingIdentifier !== incomingIdentifier) return config;

                upserted = true;
                return Object.assign(config, {
                  // Merge only the properties missing from the existing config
                  // as settings may have evolved, but awaiting debounce to be saved
                  id: toolConfig.id,
                  user_id: toolConfig.user_id,
                  updated_at: toolConfig.updated_at,
                });
              });

              if (!upserted) {
                draftConfigs.configs.push(toolConfig);
              }
            })
          );
          // Rollback on fail
          queryFulfilled.catch(patchResult.undo);
        }
      },
    },
    resetAllToolConfigs: {
      // Goal: In response to delete, we will get back some default tool configs that must replace current tool configs
      // No need to invalidate the cache, since we are replacing the entire cache with new data.
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        const { data } = await queryFulfilled;
        const { configs } = data;
        // We need to update the tool configs in the store with the new data
        const patchResult = dispatch(
          ticApi.util.updateQueryData('getToolConfigs', undefined, draft => {
            // Overwrite the entire cache with new data
            return Object.assign(draft, { configs });
          })
        );
        // Rollback on fail
        queryFulfilled.catch(patchResult.undo);
      },
    },
    deleteToolConfig: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        let defaultConfig: ToolConfig | undefined;
        defaultConfig = data?.config ? (data?.config as ToolConfig) : undefined; // argh, oapi->rtkq woes.

        const toolRef =
          defaultConfig?.tool_id || arg.toolId || defaultConfig?.tool_raw || arg.toolRaw;
        const meta = data?.meta;

        const patchResult = dispatch(
          ticApi.util.updateQueryData('getToolConfigs', undefined, draftConfigs => {
            // When a default config is forced back from server..
            // replace the "deleted" config with the default config
            if (defaultConfig) {
              draftConfigs.configs = draftConfigs.configs.map(config => {
                const existingToolRef = config?.tool_id || config?.tool_raw;

                if (existingToolRef === toolRef && defaultConfig) {
                  return defaultConfig;
                }
                return config;
              });
              // .. or just filter out
            } else {
              draftConfigs.configs = draftConfigs.configs.filter(config => {
                return toolRef !== (config.tool_id || config.tool_raw);
              });
            }

            dispatch(
              showToast({
                // If adding title, get proper name from store pre-delete
                // title: `${displayName} reset`,
                message: meta.is_predefined_config
                  ? 'Recommended customizations applied'
                  : 'Customizations reset',
                type: 'success',
                dismissAfter: 1_500,
              })
            );
          })
        );
        // Rollback on fail
        queryFulfilled.catch(patchResult.undo);
      },
    },
    getDefaultToolConfigs: {
      providesTags: ['DefaultToolConfigs'],
    },
    /**
     * Memories
     */
    getMemories: {
      /**
       * Memory settings/filtering/tool configs is done client-side now, so keep memory query
       * simple/stable to avoid excessive re-fetching (e.g, filter or tool configs cause `getTicQuery` to change)
       * @deprecated No longer need this since HTTP params are now just these 4 params
       */
      // serializeQueryArgs: ({ queryArgs }) => {
      //   const { startDate, endDate, page, limit } = queryArgs;
      //   return { startDate, endDate, page, limit };
      // },
      /**
       * After TIC Memories are loaded, determine which Timely/legacy Memories to load based on `tracker_source`'s seen.
       */
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          // Identify which platforms we've seen Memories from
          const memories = data.memories ?? [];
          const seenPlatforms = uniq(memories.map(memory => memory.tracker_source).filter(Boolean));
          const suggestTrackerExclusion: Array<'macOS' | 'Windows' | 'Linux'> = seenPlatforms.map(
            platform => {
              if (platform === 'mac') return 'macOS' as const;
              if (platform === 'windows') return 'Windows' as const;
              if (platform === 'linux') return 'Linux' as const;
              console.error(`Unknown platform from tracker_source: ${platform}`);
              return platform as 'macOS';
            }
          );

          const startDate = fromISO(arg.startDate);
          const endDate = fromISO(arg.endDate);

          // Find midway point between start and end date to use as YYYY-MM-DD date
          // This is kinda hackish, but serves current use-case
          const probablyTheYYYYMMDDdate = startDate
            .plus({ days: endDate.diff(startDate, 'days').as('days') / 2 })
            .toISODate();

          dispatch(
            rawTicApi.endpoints.getTimelyLegacyMemories.initiate({
              date: probablyTheYYYYMMDDdate,
              exclude_tracker_entries: suggestTrackerExclusion,
            })
          );
        } catch (error) {}
      },
      // Individually tag each Memory in the result so we can invalidate them individually.
      providesTags: result =>
        result?.memories
          ? [...result.memories.map(({ id }) => ({ type: 'Memory' as const, id })), 'Memory']
          : ['Memory'],
    },
    deleteMemories: {
      // We do optimistic removal of memories from the store on delete.

      async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
        const state = getState() as IState; // Type error, but we know it's IState
        const { getMemoryQueryParams } = await getMemoriesSelectors();
        const memoriesQuery = getMemoryQueryParams(state);
        // Immediately remove memories from the store
        const patchResult = dispatch(
          ticApi.util.updateQueryData('getMemories', memoriesQuery, draft => {
            const memories = draft?.memories?.filter(memory => !arg.body.includes(memory.id));
            return Object.assign(draft, { ...draft, memories });
          })
        );
        // Rollback on fail
        queryFulfilled.catch(patchResult.undo); // Rollback on fail
      },
    },
    getTasks: {
      extraOptions: {
        maxRetries: 1,
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          // This endpoint is called over and over with different filter strings.
          // However, we want to keep and display all fetches tasks regardless of filter string.
          // So, listen to these queries and append tasks to the store.
          const { data } = await queryFulfilled;
          const tasks = data?.tasks.filter(
            (task): task is PresentableTaskResource => task.type === 'task'
          );
          if (tasks) {
            dispatch(appendTasks({ tasks }));
          }
        } catch (error) {}
      },
    },
    searchTasks: {
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          const tasks = data?.tasks.filter(
            (task): task is PresentableTaskResource => task.type === 'task'
          );
          if (tasks) {
            dispatch(appendTasks({ tasks }));
          }
        } catch (error) {}
      },
    },

    // Note generation endpoints
    requestHourNoteGeneration: {
      async onQueryStarted({ body }, { queryFulfilled, dispatch }) {
        try {
          // Set loading state
          const { updateHourDraft } = await getHoursActions();
          dispatch(updateHourDraft({ _shouldRegenerateNote: false, _isGeneratingNote: true }));

          await queryFulfilled;

          // Log analytics
          timelyAnalytics.trackTic('TIC+AI Click TA Autofill Note', { hour_id: body.hour?.id });
          debug.template.ai('Note generation request submitted', { logLevel: LogLevel.Debug });
        } catch (error) {
          debug.template.ai('Note generation request failed', { error });
        }
        // NB! Intentionally leave _isGeneratingNote: true;
        // Pusher message will set it to false
      },
    },

    requestSuggestionNoteGeneration: {
      async onQueryStarted(args, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;

          // Log analytics
          const suggestion_count = Array.isArray(args.body.suggestion_ids)
            ? args.body.suggestion_ids.length
            : 1;

          timelyAnalytics.trackTic('TIC+AI Click Autofill Suggestion Notes', {
            suggestion_count,
          });

          const message = `Generating notes for ${suggestion_count} suggestion(s)`;
          dispatch(showToast({ message, logLevel: LogLevel.Debug }));
          debug.template.ai(message, { args });
        } catch (error) {
          dispatch(
            showToast({ message: 'Could not re-generate notes, please try again', type: 'danger' })
          );
          debug.template.ai('Suggestion note request failed', { args, error });
        }
      },
    },

    // Timesheet Proposals
    getTimesheetProposals: {
      providesTags: ['TimesheetProposals'],
    },
    getTimesheetProposal: {
      providesTags: (result, error, arg) => [
        { type: 'TimesheetProposal', id: arg.id },
        'TimesheetProposal',
      ],
    },
    deleteTimesheetProposal: {
      invalidatesTags: ['TimesheetProposals'],
    },
    // TimesheetProposal entry CRUD and the proposal status update are served by the
    // Rails-backed `timelyApi`, not TIC: they optimistically patch the timelyApi
    // `getTimesheetProposal` cache (entry ids are Rails bigint-as-string), and the
    // inline "create project → assign to entry" flow dispatches
    // `timelyApi.patchTimesheetProposalEntry`. The TIC endpoints remain in the
    // generated artifact but carry no custom optimistic enhancement here.
    triggerTimesheetProposalGeneration: {
      invalidatesTags: ['TimesheetProposals'],
      onQueryStarted: async args => {
        const { metadata } = args.body;
        timelyAnalytics.trackTic('TA2 Requested AI Timesheet', {
          interval_minutes: metadata?.interval_minutes,
          effort_level: metadata?.effort_level,
          provider: metadata?.provider,
          note_language: metadata?.note_language,
        });
      },
    },

    // Project Suggestions
    getProjectSuggestions: {
      providesTags: ['ProjectSuggestions'],
    },
    updateProjectSuggestionStatus: {
      invalidatesTags: ['ProjectSuggestions'],
    },
  },
});
